import { apiBomUrl } from "src/common/apiUrl";
import { ZUid } from "src/types/zUid";
import { rest, stdParamsSerializer } from "src/common/rest";
import { FnLoad } from "src/components/tables/TableStore";
import z from "zod";
import { zIdName, ZIdName } from "src/types/ZIdName";
import {
  zBom3MatrixSettings,
  ZBom3MatrixSettings,
  zBom3Settings,
  ZBom3Settings,
} from "./ZBom3Settings";
import { Bom3Filter, zBom3, ZBom3, zPermission, ZPermission } from "./ZBom3";

const urlBom3Settings = (path: string = "") =>
  apiBomUrl(`/v4/api/settings${path}`);

export const loadBom3Settings = async (
  objectId: ZUid,
): Promise<ZBom3Settings> => {
  const resp = await rest.get(urlBom3Settings(), { params: { objectId } });
  const rawResult = zBom3Settings
    .omit({ sizeVariations: true, commonSumma: true })
    .parse(resp.data);
  rawResult.consumptionBySize = rawResult.consumptionBySize ?? false;
  // Заполнить поле sizeVariations, которое нужно только для работы формы.
  return {
    ...rawResult,
    sizeVariations: !!rawResult.objSizeLine && !!rawResult.objSizeName,
    commonSumma:
      !!rawResult.attBomTotalAmount && !!rawResult.attBomPositionAmount,
  };
};

export const saveBom3Settings = async (
  objectId: ZUid,
  serviceId: ZUid,
  settings: ZBom3Settings,
): Promise<void> => {
  // Невидимые поля здесь отсутствуют (их исключает компонент Form)
  // Остаётся лишнее поле sizeVariations, но оно игнорируется сервером.
  await rest.put(urlBom3Settings(), settings, {
    params: { objectId, serviceId },
  });
};

export const loadBom3Matrix = async (
  bomSectionId: ZUid,
  materialTypeId: ZUid,
): Promise<ZBom3MatrixSettings> => {
  const resp = await rest.get(urlBom3Settings("/matrix"), {
    params: { bomSectionId, materialTypeId },
  });
  return zBom3MatrixSettings.parse(resp.data);
};

export const loadBom3ProductCategoryMatrix = async (
  bomSectionId: ZUid,
  productCategoryId: ZUid,
): Promise<ZBom3MatrixSettings> => {
  const resp = await rest.get(urlBom3Settings("/matrix/product-category"), {
    params: { bomSectionId, productCategoryId },
  });
  return zBom3MatrixSettings.parse(resp.data);
};

export const loadBom3Permissions = async (
  objectId: ZUid,
): Promise<ZPermission[]> => {
  const resp = await rest.get(urlBom3Settings("/permissions"), {
    params: { objectId },
  });
  return zPermission.array().parse(resp.data);
};

// ---- copy

export const copyBom3 = async (
  bomEntityIdsFrom: ZUid[],
  modelEntityIdsTo: ZUid[],
): Promise<void> => {
  const body = { bomEntityIdsFrom, modelEntityIdsTo };
  const url = apiBomUrl(`/v4/api/copy`);
  await rest.post(url, body);
};

// -------------

const urlBom3 = (path: string = "") => apiBomUrl(`/v4/api/bom${path}`);

export const loadBom3 = async (bomEntityId: ZUid): Promise<ZBom3> => {
  const resp = await rest.get(urlBom3(), { params: { bomEntityId } });
  return zBom3.parse(resp.data);
};

const zBom3List = z.object({
  content: zBom3.array(),
  totalElements: z.number(),
});

export const loaderBom3 =
  (modelEntityId: ZUid): FnLoad<ZBom3, Bom3Filter> =>
  async ({ pageSize, page }) => {
    const resp = await rest.get(urlBom3("/list"), {
      params: {
        modelEntityId,
        page,
        size: pageSize,
      },
    });
    const { content, totalElements } = zBom3List.parse(resp.data);
    return {
      rows: content,
      totalItems: totalElements,
    };
  };

export const createBom3 = async (
  modelEntityId: ZUid,
  data: ZBom3,
): Promise<void> => {
  await rest.post(urlBom3(), data, { params: { modelEntityId } });
  // Сейчас ответ не используется.
  // Но если он понадобится, то возникнет проблема, связанная с тем, что имя состояния приходит null
};

export const updateBom3 = async (
  bomEntityId: ZUid,
  data: ZBom3,
): Promise<void> => {
  await rest.put(urlBom3(), data, { params: { bomEntityId } });
};

export const deleteBom3 = async (bomEntityId: ZUid[]): Promise<void> => {
  await rest.delete(urlBom3(), {
    params: { bomEntityId },
    paramsSerializer: stdParamsSerializer,
  });
};

export const loadBom3MaterialSizes = async (
  bomEntityId: ZUid,
  materialId: string,
): Promise<ZIdName[]> => {
  const resp = await rest.get(urlBom3("/material-sizes"), {
    params: { bomEntityId, materialId },
  });
  return zIdName.array().parse(resp.data);
};
